import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import LoadingSpinner from "../../shared-components/LoadingSpinner";
import FilterIcon from "../../shared-components/icons/FilterIcon";
import { ReportSummaryItemApi } from "../../api/Reports";
import Badge from "../../shared-components/Badge";
import { formattedDate } from "../../utils";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { useFamaFlags } from "../../api/FamaFlags";
import { getSummaryPath } from "../../reports/useNavigateToSummaryWithState";
import {
  OpenAmendmentAdminCompletedButton,
  ReportDeleteAdminCompletedButton,
  ReportLogAdminCompletedButton,
  ReportRegeneratePDFAdminCompletedButton,
  ReportRerunPersonPDFAdminCompletedButton,
} from "./TableAdminCompletedButtons";
import PDFDownloadWrapper from "../../reports/PDFDownloadWrapper";
import { usePermissionValidator } from "../../api/UserPermissionValidators";
import ReactTooltip from "react-tooltip";
import CopyIcon from "../../shared-components/CopyIcon";

interface TableProps {
  data: ReportSummaryItemApi[] | undefined;
  header: Array<{ name: string; sortController?: string }>;
  isLoading: boolean;
  hasFilters: (value: string) => boolean;
  openFilter: (value: string) => void;
}

const Table = ({
  data,
  header,
  isLoading,
  openFilter,
  hasFilters,
}: TableProps) => {
  const [tableData, setTableData] = useState<ReportSummaryItemApi[]>([]);
  const location = useLocation();
  const { v3UiCompletedListWith360 } = useFamaFlags();
  const { canRerunReport, canRegeneratePDF, canAmend } =
    usePermissionValidator();

  useEffect(() => {
    if (data) setTableData(data);
  }, [data]);

  return (
    <>
      <div>
        <div className="flex text-gray-500 rounded-t-xl border shadow shadow-gray-300">
          {header.map((header) => (
            <div
              className="flex rounded-t-xl items-center p-3 text-xs leading-4 font-medium bg-gray-50 uppercase flex-1"
              key={header.name}
            >
              {header.name}
              {header.sortController && (
                <FilterIcon
                  width={16}
                  className={`my-div ${
                    hasFilters(header.sortController) ? "fill-ourGreen" : ""
                  }`}
                  onClick={() => openFilter(header.sortController!)}
                ></FilterIcon>
              )}
            </div>
          ))}
        </div>
        <div className="bg-white border-l border-r rounded-b-xl shadow shadow-gray-300">
          {isLoading ? <LoadingSpinner /> : ""}
          {tableData.length === 0 && !isLoading ? (
            <div className="flex justify-center p-4 ">No Completed Reports</div>
          ) : (
            tableData.map((report, i) => (
              <div key={`completed-${i}`}>
                <div className="flex border-b text-xs leading-4 font-normal py-4 hover:bg-gray-50 text-gray-700">
                  <div className="flex px-3 flex-1  items-center truncate text-ellipsis w-32">
                    <div>
                      <div className="font-semibold underline cursor-pointer pb-1">
                        <Link
                          to={getSummaryPath(report, v3UiCompletedListWith360)}
                          state={{
                            originUrl: `${location.pathname}${location.search}`,
                            report,
                          }}
                          className="underline cursor-pointer font-semibold"
                        >
                          {report.full_name}
                        </Link>
                      </div>
                      <div className="pb-1">{report.company_name}</div>
                      <div className="flex items-center gap-1">
                        <CopyIcon textToCopy={report.report_uuid} />
                        {report.report_uuid}
                      </div>
                    </div>
                  </div>
                  <div className="flex px-3 flex-1  items-center truncate text-ellipsis w-32">
                    <div>{report.parent_company_name}</div>
                  </div>
                  <div className="flex px-3 flex-1  items-center truncate text-ellipsis w-32">
                    <div>
                      <div className="pb-1">
                        {formattedDate(report.finish_at)}
                      </div>
                      <div>{formattedDate(report.created_at)}</div>
                    </div>
                  </div>
                  <div className="flex px-3 flex-1  items-center truncate text-ellipsis w-32">
                    <div>
                      {report.sla_met === undefined ||
                      report.sla_met === null ? (
                        <Badge color="gray">N/A</Badge>
                      ) : report.sla_met ? (
                        <Badge color="green">Yes</Badge>
                      ) : (
                        <Badge color="red">No</Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex px-3 flex-1  items-center truncate text-ellipsis w-32">
                    <div>
                      <div className="font-semibold underline  mb-2">
                        {report.blueprint_name}
                      </div>
                      <div>{report.behavior_kit_name}</div>
                    </div>
                  </div>
                  <div className="flex px-3 flex-1  items-center truncate text-ellipsis w-32">
                    <div className="flex gap-1">
                      {canRerunReport && (
                        <ReportRerunPersonPDFAdminCompletedButton
                          report={report}
                        />
                      )}
                      {canAmend && (
                        <OpenAmendmentAdminCompletedButton
                          reportUUID={report.report_uuid}
                        />
                      )}
                      <PDFDownloadWrapper report_uuid={report.report_uuid}>
                        <>
                          <ReactTooltip
                            className="hover:visible:opacity-1 h-9"
                            effect="solid"
                            id={"rt-download-pdf"}
                          />
                          <ArrowDownTrayIcon
                            className="w-6 text-gray-400 border border-gray-300 shadow-sm rounded-md p-1 cursor-pointer"
                            data-tip="Download PDF"
                            data-for="rt-download-pdf"
                          />
                        </>
                      </PDFDownloadWrapper>
                      <ReportLogAdminCompletedButton
                        reportUUID={report.report_uuid}
                      />
                      {canRegeneratePDF && (
                        <ReportRegeneratePDFAdminCompletedButton
                          reportUUID={report.report_uuid}
                        />
                      )}
                    </div>
                  </div>
                  <div className="flex px-3 flex-1  items-center truncate text-ellipsis w-32">
                    <div>
                      <ReportDeleteAdminCompletedButton
                        reportUUID={report.report_uuid}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default Table;
